/**
 * La conversación de la empresa con su gestoría — `OD-0067` D-05 y D-06
 * (ACCEPTED 2026-10-28), con `RC-0025`. Como un hilo de WhatsApp, sin tiempo
 * real: se lee al abrirla y después de cada mensaje.
 *
 * ```text
 * LA EMPRESA    Configuracion > Asesoria (organizationId null), y dentro de una
 *               factura o una compra, solo lo que habla de ella
 * LA GESTORIA   en su panel, en la fila del cliente (organizationId del cliente)
 * ```
 *
 * ## Lo que esta pantalla NO decide
 *
 * - **Ni quién la ve**: sin el plan, sin el rol o sin gestoría con acceso, la
 *   API no contesta 200 y la tarjeta no se pinta.
 * - **Ni de qué lado se está**: lo dice la API (`lado`), por el rol.
 * - **Ni a quién va el correo**: `destinatario` lo elige la API (D-06). Los
 *   adjuntos van en el correo de quien escribe; Faktelio no los ve.
 */
import { useCallback, useEffect, useState } from "react";

import { apiConversacion, ESTADO_VALIDACION, type ConversacionConLaAsesoria as Conversacion, type MensajeDeLaConversacion } from "../api/cliente";
import { t } from "../i18n/es";
import { AreaTexto, Aviso, Boton, Tarjeta } from "../ui/primitivas";

type Documento = { readonly tipo: "factura" | "compra"; readonly id: string; readonly etiqueta: string };

const HORA = new Intl.DateTimeFormat("es-ES", { timeZone: "Europe/Madrid", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

/** D-06: el correo de quien escribe, con el destinatario puesto. El adjunto lo añade esa persona. */
export function enlaceDeCorreo(destinatario: string, asunto: string, cuerpo: string): string {
  return `mailto:${encodeURIComponent(destinatario).replace(/%40/g, "@")}?subject=${encodeURIComponent(asunto)}&body=${encodeURIComponent(cuerpo)}`;
}

function quien(m: MensajeDeLaConversacion, c: Conversacion, empresa: string | null): string {
  if (m.propio) return t.conversacionAsesoria.tu;
  if (m.autor !== null) return m.autor;
  // La gestoria no recibe el correo de nadie de la empresa (OD-0013).
  return m.lado === "CLIENT" ? (empresa ?? t.conversacionAsesoria.laEmpresa) : (c.asesoria ?? t.conversacionAsesoria.tuAsesoria);
}

export function ConversacionConLaAsesoria({
  organizationId,
  empresa = null,
  documento = null,
  hayConexion,
  onSesionCaducada,
  onLeida,
}: {
  /** `null`: la de la empresa activa. Con un id: la gestoría, desde su panel. */
  readonly organizationId: string | null;
  /** El nombre de la empresa, para la gestoría: así se firma lo que escribe la empresa. */
  readonly empresa?: string | null;
  /** Dentro de una factura o compra: solo lo que habla de ella, y lo nuevo va unido a ella. */
  readonly documento?: Documento | null;
  readonly hayConexion: boolean;
  readonly onSesionCaducada?: () => void;
  readonly onLeida?: () => void;
}) {
  const [c, setC] = useState<Conversacion | null>(null);
  const [texto, setTexto] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const api = apiConversacion(organizationId);

  const leida = useCallback(
    async (vista: Conversacion): Promise<void> => {
      if (vista.noLeidos === 0) return;
      const r = await apiConversacion(organizationId).marcarLeida();
      if (r.ok) onLeida?.();
    },
    [organizationId, onLeida],
  );

  useEffect(() => {
    let vivo = true;
    void apiConversacion(organizationId)
      .ver()
      .then((r) => {
        if (!vivo) return;
        if (r.ok) {
          setC(r.datos);
          void leida(r.datos);
        } else if (r.estado === 401) onSesionCaducada?.();
        // Sin plan, sin rol o sin gestoria: no hay conversacion que ensenar.
      });
    return () => {
      vivo = false;
    };
  }, [organizationId, leida, onSesionCaducada]);

  if (c === null) return null;

  const mensajes = documento === null ? c.mensajes : c.mensajes.filter((m) => m.documento?.tipo === documento.tipo && m.documento.id === documento.id);

  async function enviar(): Promise<void> {
    setOcupado(true);
    setError(null);
    setMensaje(null);
    const r = await api.enviar(texto, documento === null ? null : { tipo: documento.tipo, id: documento.id });
    setOcupado(false);
    if (r.ok) {
      setC(r.datos);
      setTexto("");
      return;
    }
    if (r.estado === 401) return onSesionCaducada?.();
    const campo = r.estado === ESTADO_VALIDACION ? r.error?.campos.texto : undefined;
    setError(campo !== undefined ? (t.conversacionAsesoria.errores[campo] ?? t.comun.errorGenerico) : null);
    if (campo === undefined) setMensaje(t.conversacionAsesoria.noSeHaEnviado);
  }

  async function retirar(id: string): Promise<void> {
    if (!window.confirm(t.conversacionAsesoria.confirmarRetirar)) return;
    setOcupado(true);
    const r = await api.retirar(id);
    setOcupado(false);
    if (r.ok) setC(r.datos);
    else setMensaje(t.conversacionAsesoria.noSeHaRetirado);
  }

  const correo =
    c.destinatario === null
      ? null
      : enlaceDeCorreo(
          c.destinatario,
          c.lado === "ADVISOR" ? t.conversacionAsesoria.asuntoDeLaGestoria(empresa) : t.conversacionAsesoria.asuntoDeLaEmpresa(documento?.etiqueta ?? null),
          t.conversacionAsesoria.cuerpoDelCorreo(window.location.origin),
        );

  return (
    <Tarjeta titulo={documento === null ? t.conversacionAsesoria.titulo(c.lado === "ADVISOR" ? empresa : c.asesoria) : t.conversacionAsesoria.tituloDelDocumento}>
      {mensajes.length === 0 ? (
        <p className="ayuda-neutra">{documento === null ? t.conversacionAsesoria.vacia : t.conversacionAsesoria.vaciaDelDocumento}</p>
      ) : (
        <ol className="conversacion">
          {mensajes.map((m) => (
            <li key={m.id} className={m.propio ? "mensaje mensaje-propio" : "mensaje"}>
              <p className="mensaje-cabecera">
                <strong>{quien(m, c, empresa)}</strong> <span className="ayuda-neutra">{HORA.format(new Date(m.enviadoEn))}</span>
              </p>
              {m.documento !== null && documento === null ? (
                <p className="ayuda-neutra">{t.conversacionAsesoria.sobre(m.documento.tipo, m.documento.etiqueta)}</p>
              ) : null}
              {m.retirado ? (
                <p className="mensaje-retirado">{t.conversacionAsesoria.retirado}</p>
              ) : (
                <p className="texto-con-saltos">{m.texto}</p>
              )}
              {m.propio && !m.retirado ? (
                <Boton variante="terciario" deshabilitado={!hayConexion || ocupado} onClick={() => void retirar(m.id)}>
                  {t.conversacionAsesoria.retirar}
                </Boton>
              ) : null}
            </li>
          ))}
        </ol>
      )}
      <AreaTexto
        id={`conversacion-${organizationId ?? "propia"}-${documento?.id ?? "todo"}`}
        etiqueta={documento === null ? t.conversacionAsesoria.escribir : t.conversacionAsesoria.escribirDelDocumento(documento.tipo, documento.etiqueta)}
        valor={texto}
        onChange={setTexto}
        filas={3}
        ayuda={t.conversacionAsesoria.ayuda}
        error={error}
      />
      <div className="acciones">
        <Boton onClick={() => void enviar()} deshabilitado={!hayConexion || ocupado || texto.trim() === ""}>
          {ocupado ? t.conversacionAsesoria.enviando : t.conversacionAsesoria.enviar}
        </Boton>
        {correo !== null ? (
          <a className="boton boton-secundario enlace-boton" href={correo}>
            {t.conversacionAsesoria.mandarPorCorreo}
          </a>
        ) : null}
      </div>
      {correo !== null ? <p className="ayuda-neutra">{t.conversacionAsesoria.ayudaCorreo(c.destinatario ?? "")}</p> : null}
      {mensaje !== null ? <Aviso tono="error">{mensaje}</Aviso> : null}
    </Tarjeta>
  );
}

/** D-05: el aviso de Inicio cuando la asesoría ha escrito y no se ha leído. */
export function AvisoDeConversacion({ noLeidos, onAbrir }: { readonly noLeidos: number; readonly onAbrir: () => void }) {
  if (noLeidos === 0) return null;
  return (
    <Aviso tono="info">
      {t.conversacionAsesoria.avisoInicio(noLeidos)}{" "}
      <Boton variante="terciario" onClick={onAbrir}>
        {t.conversacionAsesoria.abrir}
      </Boton>
    </Aviso>
  );
}
